/**
 * Case-related strings.
 */
import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import IconButton from '../ui/IconButton/IconButton';
import Icons from '../ui/IconButton/Icons';
import ArticleButton from '../ui/ArticleButton/ArticleButton';
import RoutedIconButton from "../ui/Buttons/RoutedIconButton";

export default {
    d_name: 'Case Name',
    h_name: '<div><p>The name of the case is used both in the Case listing, as well as on the client invoices.</p><p>As such, it is best practice to put the type of case as the case name.</p><p>Note that the case name will <b>always</b> be given with the client name(s) anywhere in Outlaw Practice, so putting the client name in the case name is redundant.</p></div>',

    d_statusColumn: 'Status',
    h_statusColumn: <div>
        <div>The status of the case.</div>
        <div>Status icons are represented as colored folders:
            <ul className='u-icon-list'>
                <li><IconButton type={Icons.CASE_OPEN} label='Open'/></li>
                <li><IconButton type={Icons.CASE_CLOSED} label='Closed'/></li>
                <li><IconButton type={Icons.CASE_WITHDRAWN} label='Withdrawn'/></li>
                <li><IconButton type={Icons.CASE_PENDING} label='Pending (not started)'/></li>
                <li><IconButton type={Icons.CASE_ON_HOLD} label='On Hold (open, but paused)'/></li>
            </ul>

            <p>By default, only open cases are shown. Click on the Show Closed checkbox on the right to show
                all other cases.</p>
        </div>
    </div>,

    d_nameColumn: 'Name',
    h_nameColumn: 'The name of the case',

    d_lead: 'Lead',
    h_lead: <div>
        <p>Links this case to the related lead (if any).</p>
        <p>To link a lead to this case, you can choose a lead in the dropdown list.</p>
        <p>If no lead exists yet, you can create a new lead in the Sales page page, then come back and link it here.</p>
        <p>Alternatively, on the Cases page, you can click on the <FontAwesomeIcon icon={Icons.ADD.reactIcon}
                                                                                   color='navy'/> Add
            button in the Lead column for this case, which will create a new lead pre-linked to the case.</p>
    </div>,

    d_caseType: 'Case Type',
    h_caseType: <div>
        <p>Choose the type of case that best matches what the client needs help with.
            This was originally set in the Lead, if available.
        </p>
        <p>New case types can be created in the Case Types page of the Configuration tab.</p>
        <div>
            <RoutedIconButton
                id='link-to-case-types'
                type={Icons.CASE}
                color='navy'
                label='Case Type Configuration'
                route='/config/cases/case-types'
            />
        </div>
    </div>,

    d_code: 'Code',
    h_code: <div>
        <p>Each case has a unique code that can be used for your reference.</p>
        <p>A popular naming scheme is [year]-[case type code]-[serial number], e.g. 2022-D-0002. In this case,
            the year is 2022, the case type is D (for Divorce), and the 0002 means this is the second Divorce case in
            2022.</p>
        <p>Case codes are part of the configuration of the case types.</p>
        <div>
            <RoutedIconButton
                id='link-to-case-types'
                type={Icons.CASE}
                color='navy'
                label='Case Type Configuration'
                route='/config/cases/case-types'
            />
        </div>
    </div>,

    d_clientNames: 'Client Names',
    h_clientNames: <div>
        <p>This is a list of clients involved with this case.</p>
        <p>You can either select existing contacts, or you can enter new ones
            directly by typing in the full name of the contact.
        </p>
        <p>Once you have saved this Lead, you will be able to edit their details
            further in the Contact tab.
        </p>
        <div>
            <RoutedIconButton
                id='link-to-contacts'
                type={Icons.ADDRESS_BOOK}
                color='navy'
                label='Contacts'
                route='/contacts'
            />
        </div>
    </div>,

    d_billTo: 'Bill To',
    h_billTo: <div>
        <p>By default, invoices for this case are addressed to the client(s) above.</p>
        <p>If the case is paid by a third party — for example a legal insurance provider
            or a service such as Texas Legal — choose that contact here. Invoices for this
            case will then be addressed to the bill-to contact (its billing name and address)
            instead of the client.
        </p>
        <p>Leave this blank to address invoices to the client(s).</p>
    </div>,

    d_assignedTo: 'Assigned To',
    h_assignedTo: 'Team members assigned to this case.',

    d_feeTypes: 'Fee Type(s)',
    h_feeTypes: <div style={{display: 'flex', flexDirection: 'column'}}>
        <p>Fee types may be combined for a single case.</p>
        <p>Fee types can be changed until an invoice for this case has been finalized, although flat fees can always
            be added to a case (although not removed once used).</p>
        <p>Each fee type has its own configuration. Scroll to the bottom of the page to see them.</p>
    </div>,

    d_minimumTrust: 'Minimum Trust',
    h_minimumTrust: '<div><p>Specify the minimum amount of money the client must keep in trust at all times.</p><p>This integrates with several parts of the product:</p><ul><li>When processing invoices, the product will calculate the replenishment amount for the trust based on this value and the amount of money in the case trust and client trust(s).</li><li>In both tasks and cases, there is a widget with UNBILLED, DUE and TRUST at the top of the page. If the client is running out of trust, there will be a message accompanying this widget to let you know to be careful with the amount of work you do before replenishing the trust.</li><li>In the Cases page, you will see a Trust column on the right with the current amount in trust. If the amount in trust is less than the Minimum Trust amount, the background of the Trust column will have green striping to indicate that the trust is below the minimum amount.</li></ul></div>',

    d_maxFunding: 'Max Funding',
    h_maxFunding: '<div><p>If there is a maximum amount of funding available for this case, specify it here.</p><p>This could be due to limited funds available to the client, or this could be due to the funding source, e.g. a legal insurance provider.</p><p>For example, if you are working on a case that has a maximum payout of $5,000 from the legal insurance company, you would enter $5000 here.</p><p>Another example would be a limited scope engagement, where the client has decided to engage you for services up to $2,000.</p><p>This information is used to alert users that they are working on a case with restricted funding, and that their efforts must be constrained by the money available.</p></div>',

    d_disposition: 'Disposition',
    h_disposition: <div>
        The disposition, or status, of the case.

        <ul className='u-icon-list'>
            <li><IconButton type={Icons.CASE_OPEN}/>Open means that the case is being
                actively worked.
            </li>
            <li><IconButton type={Icons.CASE_CLOSED}/>Closed means that the case is
                finished, and no more work is being done. Cases with a closed
                status will not show up in the list of cases when creating new tasks.
            </li>
            <li><IconButton type={Icons.CASE_WITHDRAWN}/>Closed (Withdrawn) means that
                the case is finished, and that you withdrew from it. No more work
                is being done. Cases with a closed status will not show up in the list of cases when creating new
                tasks.
            </li>
            <li><IconButton type={Icons.CASE_PENDING}/>Pending (not started) means that
                the case is open, but work has not yet started, generally due to something
                outside of your control.
            </li>
            <li><IconButton type={Icons.CASE_ON_HOLD}/>On Hold (open, but paused) means
                that the case is open, but is not actively being worked. This could be the case if
                the client has run out of money and needs time to replenish their trust (or pay existing bills)
                before new work can begin.
            </li>
        </ul>
    </div>,

    d_startingBilled: 'Starting Billed',
    h_startingBilled: '<div><p>If this case was imported from another system, how much had been billed before being imported into Outlaw?</p><p>This number is used when calculating analytics, specifically for recovery rates (how much of what you billed did you collect?).</p></div>',

    d_startingCollected: 'Starting Collected',
    h_startingCollected: '<div><p>If this case was imported from another system, how much had been collected before being imported into Outlaw?</p><p>This number is used when calculating analytics, specifically for recovery rates (how much of what you billed did you collect?).</p></div>',

    d_court: 'Court',
    h_court: '<div><p>The court where this case is being heard.</p><p>For example: "San Antonio Municipal Court" or "U.S. Court of Appeals".</p></div>',

    d_caseNumber: 'Case #',
    h_caseNumber: '<div><p>Depending on your location and the court, this is the case number, docket number or cause number for this case.</p></div>',

    d_totalBilled: 'Total Billed',
    d_totalPaid: 'Total Paid',

    d_stats: 'Stats',
    h_stats: '<div><p>Ever wonder how much an average divorce cost? We look at all of your cases (including open cases), and compute the lifetime total, average and median cost. We also show you the total billed and paid over all cases so that you can determine if this type of case is losing you money!</p></div>',

    d_pricePlot: 'Price Plot',
    h_pricePlot: <div>
        <p>Plot total cost for each case of this type.</p>
        <ArticleButton label='Case Type Price Plot' article='/settings/case-types/'/>
    </div>
};
